import React, { useCallback } from 'react';
import { InlineNotification, Modal } from '@carbon/react';
import { useCrudConfig } from './provider';

export interface CrudDeleteModalProps {
  open: boolean;
  heading: string;
  label: string;
  deleting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: () => Promise<unknown>;
}

/**
 * A rejected delete is an EXPECTED outcome, not a crash: the server refuses to delete
 * rows that are still referenced. So the confirmation swallows the rejection, keeps
 * itself open, and renders the reason. Closing on failure — what twelve of thirteen
 * pages currently do — tells the user the row is gone when it is not.
 */
export const CrudDeleteModal = React.memo(function CrudDeleteModal(props: CrudDeleteModalProps) {
  const { translate } = useCrudConfig();
  const { onConfirm, onClose } = props;

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
      onClose();
    } catch {
      // Reason rendered from errorMessage below; the modal stays open.
    }
  }, [onConfirm, onClose]);

  return (
    <Modal
      open={props.open}
      danger
      size={'sm'}
      modalHeading={props.heading}
      modalLabel={props.label}
      primaryButtonText={props.deleting ? `${translate('common.loading')}...` : translate('actions.delete')}
      secondaryButtonText={translate('actions.cancel')}
      primaryButtonDisabled={props.deleting}
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={handleConfirm}
    >
      {props.errorMessage && (
        <InlineNotification kind={'error'} lowContrast hideCloseButton title={''} subtitle={props.errorMessage} />
      )}
    </Modal>
  );
});
